import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { toastStore } from '@/components/ui/Toast';
import { useAppStore } from '@/app/store';
import { pickLocalImageFile } from '@/platform/capabilities';
import {
  addDayMedia,
  listDayMedia,
  MAX_DAY_MEDIA_BYTES,
  removeDayMedia,
  resolveDayMediaUrl,
} from '@/platform/dayMedia';
import { isSupportedImageFile } from '@/platform/images';
import type { DayMediaRow } from '@/storage/db';
import styles from './DayMediaSection.module.css';

export function DayMediaSection({ dateKey }: { dateKey: string }) {
  const { t } = useTranslation();
  const session = useAppStore((s) => s.session);
  const space = useAppStore((s) => s.space);
  const [items, setItems] = useState<DayMediaRow[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const reload = async () => {
    if (!space) {
      setItems([]);
      return;
    }
    const rows = await listDayMedia(space.id, dateKey);
    setItems(rows);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space?.id, dateKey]);

  useEffect(() => {
    const next: Record<string, string> = {};
    const blobUrls: string[] = [];
    for (const row of items) {
      const url = resolveDayMediaUrl(row);
      if (url) {
        next[row.id] = url;
        if (url.startsWith('blob:')) blobUrls.push(url);
      }
    }
    setUrls(next);
    return () => {
      for (const url of blobUrls) URL.revokeObjectURL(url);
    };
  }, [items]);

  const attach = async () => {
    if (!session || !space) return;
    const file = await pickLocalImageFile();
    if (!file) return;
    if (!isSupportedImageFile(file)) {
      toastStore.show({ type: 'error', message: t('dayMedia.unsupported') });
      return;
    }
    if (file.size > MAX_DAY_MEDIA_BYTES) {
      toastStore.show({ type: 'error', message: t('dayMedia.tooLarge') });
      return;
    }
    setBusy(true);
    try {
      await addDayMedia({
        spaceId: space.id,
        dateKey,
        ownerId: session.user.id,
        file,
      });
      await reload();
      toastStore.show({ type: 'success', message: t('dayMedia.added') });
    } catch {
      toastStore.show({ type: 'error', message: t('errors.generic') });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: DayMediaRow) => {
    if (!session) return;
    setBusy(true);
    try {
      await removeDayMedia(row, session.user.id);
      await reload();
    } catch {
      toastStore.show({ type: 'error', message: t('dayMedia.removeFailed') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t('dayMedia.title')}</h3>
        <Button variant="secondary" size="sm" loading={busy} onClick={() => void attach()}>
          {t('dayMedia.attach')}
        </Button>
      </div>
      <p className={styles.hint}>{t('dayMedia.hint')}</p>
      {items.length === 0 ? (
        <p className={styles.empty}>{t('dayMedia.empty')}</p>
      ) : (
        <ul className={styles.grid}>
          {items.map((row) => {
            const url = urls[row.id];
            const canDelete = !row.ownerId || row.ownerId === session?.user.id;
            return (
              <li key={row.id} className={styles.item}>
                <button
                  type="button"
                  className={styles.thumbBtn}
                  onClick={() => url && setLightbox(url)}
                  disabled={!url}
                >
                  {url ? (
                    <img src={url} alt={row.fileName} className={styles.thumb} />
                  ) : (
                    <span className={styles.missing}>…</span>
                  )}
                </button>
                {canDelete && (
                  <button
                    type="button"
                    className={styles.remove}
                    onClick={() => void remove(row)}
                    aria-label={t('dayMedia.remove')}
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {lightbox && (
        <button
          type="button"
          className={styles.lightbox}
          onClick={() => setLightbox(null)}
          aria-label={t('common.close')}
        >
          <img src={lightbox} alt="" className={styles.lightboxImg} />
        </button>
      )}
    </section>
  );
}
